<script setup lang="ts">
import { computed } from 'vue';
import CoverArtTile from '@/components/CoverArtTile.vue';
import PlaylistWithBucketLink from '@/components/DataTable/PlaylistWithBucketLink.vue';
import RecordLink from '@/components/RecordLink.vue';
import type { CurrentTrackSummary } from '@/composables/api/useCurrentTrackSummaries';
import SearchHighlight from '@/components/search/SearchHighlight.vue';
import { useQuickSearchMatches } from '@/composables/quickSearchMatches';
import { LIST_HIGHLIGHT } from '@/utils/highlightMatch';

/**
 * The Location Zone Player "Currently Playing" block (the custom
 * `currently_playing` column — card layouts and the table cell):
 *
 *   | cover | track display string – artist – album
 *   |       | current playlist
 *
 * The cover is a click-to-play target for the current track (same cover chain
 * as the track cards). The track line links to the Live Track's page, with a side-peek button;
 * the playlist line resolves the reported playlist name server-side (the
 * check-in payload carries only a NAME — a generated filename or a stale bare
 * name, never an id): custom lists open in the record inspector, generated
 * playlists open the Generated Playlist inspector, and anything unresolvable
 * toasts. Unresolved track codes fall back to the raw code — still playable
 * by code.
 */
const props = withDefaults(
    defineProps<{
        row: Record<string, unknown>;
        /** Batched lookup result for this row's current track code (null = unresolved). */
        track: CurrentTrackSummary | null;
        /**
         * The quick-search fields this row matched (SAU-591), passed in by the
         * search page. The track code is highlighted while it is drawn, which is
         * only while it has not resolved. The playlist is highlighted in its label.
         * The line that holds a match wraps.
         */
        matchedFields?: readonly string[];
    }>(),
    { matchedFields: () => [] }
);

const quickSearch = useQuickSearchMatches();
const highlightTerms = computed(() => quickSearch?.terms.value ?? []);
const trackCodeMatched = computed(() => props.matchedFields.includes('player_data_current_track_code'));
const playlistMatched = computed(() => props.matchedFields.includes('player_data_current_playlist_name'));

const currentTrackCode = computed(() => {
    const code = props.row.player_data_current_track_code;
    return typeof code === 'string' && code.trim() !== '' ? code.trim() : null;
});

const playlistName = computed(() => {
    const name = props.row.player_data_current_playlist_name;
    return typeof name === 'string' && name.trim() !== '' ? name.trim() : null;
});

// The list (and its bucket, when it only points at one) resolve and open
// inside PlaylistWithBucketLink, shared with the inspector's Current Playlist
// row so both agree on what a reported name means.
const locationZonePlayerId = computed(() =>
    typeof props.row.id === 'number' ? props.row.id : null
);

const trackLine = computed(() => {
    if (!currentTrackCode.value) return null;
    if (!props.track) return currentTrackCode.value;
    return [props.track.name ?? props.track.code, props.track.artistName, props.track.albumName]
        .filter(Boolean)
        .join(' – ');
});

/** PlayTrackButton props for the cover overlay — playable by code even unresolved. */
const coverPlay = computed(() => ({
    kind: 'live_track' as const,
    liveTrackCode: currentTrackCode.value,
    hasAudio: Boolean(currentTrackCode.value),
    title: props.track?.name ?? currentTrackCode.value,
    artist: props.track?.artistName ?? null,
    album: props.track?.albumName ?? null,
    coverUrl: props.track?.coverUrl ?? null,
    // Only a resolved check-in has records to point at; an unresolved code
    // still plays, and the player bar shows its lines as plain text.
    liveTrackId: props.track?.id ?? null,
    artistId: props.track?.artistId ?? null,
    albumId: props.track?.albumId ?? null,
}));
</script>

<template>
    <!-- No @click.stop on this container. It used to carry one, so the whole
         block ate clicks — including the empty space beside the truncated
         track line and below the playlist line — and clicking there did
         nothing instead of selecting the player. Only the parts that DO
         something stop the click now; everywhere else falls through to the
         card and selects the row. -->
    <div
        v-if="currentTrackCode || playlistName"
        class="mt-2 flex items-start gap-2.5"
        data-testid="lzp-currently-playing"
    >
        <!-- -mx-px cancels the cover tile's 1px border so the ARTWORK edge
             (not the border) sits on the shared text left edge. -->
        <div class="-ml-px w-11 shrink-0" @click.stop>
            <CoverArtTile
                :src="track?.coverUrl ?? null"
                :alt="`Cover art for ${track?.name ?? currentTrackCode ?? 'current track'}`"
                :size="44"
                :play="currentTrackCode ? coverPlay : undefined"
                :expand-testid="`lzp-currently-playing-expand-${row.id}`"
            />
        </div>
        <div class="min-w-0 flex-1 text-xs leading-snug">
            <!-- A resolved track links to the Live Track's page, with the side-peek
                 button on hover. `inline` keeps that button out of the line until then. -->
            <div v-if="trackLine && track" class="flex min-w-0">
                <RecordLink
                    entity-id="LiveTrack"
                    :record-id="track.id"
                    :label="`live track ${trackLine}`"
                    inline
                    class="hover:text-primary font-medium"
                    :title="trackLine"
                    data-testid="lzp-currently-playing-track"
                    >{{ trackLine }}</RecordLink
                >
            </div>
            <!-- An unresolved code has no record to open, so it stays a disabled line. -->
            <button
                v-else-if="trackLine"
                type="button"
                class="text-muted-foreground block max-w-full cursor-default text-left font-medium"
                :class="trackCodeMatched ? 'wrap-anywhere' : 'truncate'"
                :title="trackLine"
                disabled
                data-testid="lzp-currently-playing-track"
            >
                <SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="trackCodeMatched">{{
                    trackLine
                }}</SearchHighlight>
            </button>
            <div
                class="text-muted-foreground mt-0.5 flex min-w-0 items-baseline gap-2"
                data-testid="lzp-currently-playing-playlist-line"
            >
                <SearchHighlight :name="LIST_HIGHLIGHT" :terms="highlightTerms" :active="playlistMatched">
                    <PlaylistWithBucketLink
                        v-if="playlistName"
                        :playlist-name="playlistName"
                        :location-zone-player-id="locationZonePlayerId"
                        :wrap="playlistMatched"
                        data-testid="lzp-currently-playing-playlist"
                    />
                </SearchHighlight>
            </div>
        </div>
    </div>
</template>
